import { Component, OnInit, Inject, Optional, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormControl } from '@angular/forms';
import { map, Observable, startWith, Subject } from 'rxjs';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppState } from '../../../../../../store/reducers';
import { Store } from '@ngrx/store';
import { AlertService } from '../../../../../../commons/services/alert.service';
import { User } from '../../../../../../commons/models/user.model';
import * as UserActions from "../../../../../../store/actions/user.actions";
import { Tenants } from '../../../../../../commons/models/tenants';

@Component({
  selector: 'app-new-users',
  templateUrl: './new-users.component.html',
  styleUrls: ['./new-users.component.scss']
})
export class NewUsersComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();
  usersForm: UntypedFormGroup;
  tenantSearchControl = new FormControl();
  tenants: Tenants[] = [];
  filteredTenants: Observable<Tenants[]>;
  selectedTenants: Tenants[] = [];

  private _user: User;

  constructor(
    private store$: Store<AppState>,
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit() {
    if (this.data) {
      this.user = this.data.user;
      this.tenants = this.data.tenants || [];
    }

    this._createForm();
    this.ngOnChanges();

    this.filteredTenants = this.tenantSearchControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterTenants(value))
    );

    this.usersForm.get('role')?.valueChanges.subscribe((newRole) => {
      if(newRole !== 'manager'){
        this.selectedTenants = [];
        this.usersForm.controls['tenants'].setValue([]);
      }
    });
  }

  private _filterTenants(value: string | Tenants): Tenants[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';

    return this.tenants.filter(tenant =>
      tenant.name.toLowerCase().includes(filterValue) &&
      !this.selectedTenants.some(t => t.id === tenant.id)
    );
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private _createForm() {
    this.usersForm = this.fb.group({
      name: ["", Validators.required],
      surname: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      password: [""],
      phone: [""],
      active: [true, Validators.required],
      role: ["", Validators.required],
      tenants: [[]]
    });
  }

  private ngOnChanges() {
    if (this.usersForm) {
      this.usersForm.reset();
      if (this.user) {
        this._initFormValues(this.user);
      } else {
        this._initFormValues();
      }
    }
  }

  private _initFormValues(user?: User) {
    if (user) {
      this.usersForm.patchValue({
        name: user.name,
        surname: user.surname,
        email: user.email,
        phone: user.phone,
        active: user.active,
        role: user.role
      });

      if (user.tenants) {
        this.selectedTenants = [...user.tenants];
        this.usersForm.controls['tenants'].setValue(this.selectedTenants.map(t => t.id));
      }
    } else {
      this.usersForm.markAllAsTouched();
    }
  }

  private _prepareSaveUser(): User {
    let savingUser: User = User.fromFormGroup(this.usersForm, this.user);
    return savingUser;
  }

  save() {
    let unsavedUser = this._prepareSaveUser();
    this.usersForm.markAsDirty();
    this.store$.dispatch(UserActions.saveUser({ user: unsavedUser }));
  }

  close() {
    if (this.usersForm.dirty) {
        // Se ci sono modifiche, mostra il dialog di conferma
        this.store$.dispatch(UserActions.requestCloseUsersDialog());
      } else {
        // Se il form è pristine (nessuna modifica), chiudi
        this.store$.dispatch(UserActions.closeUserDialog());
      }
  }

  deleteUser() {
    if (this.user) {
      this.store$.dispatch(UserActions.deleteUser({ user: this.user.toDTO() }));
    }
  }

  revert() {
    this.ngOnChanges();
  }

  get user(): User {
    return this._user;
  }

  set user(user: User) {
    this._user = user;
    this.ngOnChanges();
  }

  selectTenant(event) {
    const selectedTenant: Tenants = event.option.value;

    if (selectedTenant && !this.selectedTenants.some(t => t.id === selectedTenant.id)) {
      this.selectedTenants.push(selectedTenant);
      this.usersForm.controls['tenants'].setValue(this.selectedTenants.map(t => t.id));

      this.usersForm.controls['tenants'].markAsDirty();
      this.usersForm.controls['tenants'].markAsTouched();

      // ✅ Forza il reset dell'input per evitare errori
      this.tenantSearchControl.setValue('');
    }
  }


  removeTenant(tenant: Tenants) {
    this.selectedTenants = this.selectedTenants.filter(t => t.id !== tenant.id);
    this.usersForm.controls['tenants'].setValue(this.selectedTenants.map(t => t.id));

    // ✅ Segna il form come modificato quando rimuoviamo un tenant
    this.usersForm.controls['tenants'].markAsDirty();
    this.usersForm.controls['tenants'].markAsTouched();
  }

  displayTenant(tenant?: Tenants): string {
    return tenant ? tenant.name : '';
  }
}
